import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoAddOutline,
  IoCreateOutline,
  IoSettingsOutline,
  IoStar,
  IoStarOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  createField,
  deleteField,
  listFields,
  updateField,
} from "../../../api/fieldsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import FieldInputRenderer from "../fields/FieldInputRenderer";
import ProjectFormModal from "./ProjectFormModal";
import {
  FEATURED_MAX_OPTIONS,
  PROJECT_FIELD_DEFS,
  countFeaturedProjects,
  formValueToFieldString,
  getFeaturedMax,
  getProjectTitle,
  groupProjectFields,
  projectGroupToForm,
  syncProjectCount,
} from "./projectFieldUtils";
import { useProjectsSection } from "./useProjectsSection";

export default function ProjectsManager() {
  const notify = useNotify();
  const {
    sectionId,
    pageId,
    pageName,
    loading: sectionLoading,
    error: sectionError,
    reload: reloadSection,
  } = useProjectsSection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [listTitle, setListTitle] = useState("");
  const [selectedTitle, setSelectedTitle] = useState("");
  const [featuredMaxValue, setFeaturedMaxValue] = useState("5");
  const [loadMoreText, setLoadMoreText] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  const loadFields = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listFields(sectionId);
      const list = Array.isArray(data) ? data : [];
      const sorted = list.sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
      );
      setFields(sorted);

      setListTitle(sorted.find((f) => f.key === "list_title")?.value ?? "");
      setSelectedTitle(
        sorted.find((f) => f.key === "selected_title")?.value ?? ""
      );
      setFeaturedMaxValue(
        sorted.find((f) => f.key === "featured_max")?.value ?? "5"
      );
      setLoadMoreText(
        sorted.find((f) => f.key === "load_more_text")?.value ?? ""
      );
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    if (sectionId) loadFields();
  }, [sectionId, loadFields]);

  const projects = useMemo(() => groupProjectFields(fields), [fields]);
  const featuredMax = useMemo(() => getFeaturedMax(fields), [fields]);
  const featuredCount = useMemo(
    () => countFeaturedProjects(projects),
    [projects]
  );

  const listTitleField = useMemo(
    () => fields.find((f) => f.key === "list_title"),
    [fields]
  );
  const selectedTitleField = useMemo(
    () => fields.find((f) => f.key === "selected_title"),
    [fields]
  );
  const featuredMaxField = useMemo(
    () => fields.find((f) => f.key === "featured_max"),
    [fields]
  );
  const loadMoreField = useMemo(
    () => fields.find((f) => f.key === "load_more_text"),
    [fields]
  );

  const hasSettingsChanges = useMemo(() => {
    if (listTitleField && listTitle !== (listTitleField.value ?? "")) {
      return true;
    }
    if (
      selectedTitleField &&
      selectedTitle !== (selectedTitleField.value ?? "")
    ) {
      return true;
    }
    if (
      featuredMaxField &&
      featuredMaxValue !== (featuredMaxField.value ?? "5")
    ) {
      return true;
    }
    if (loadMoreField && loadMoreText !== (loadMoreField.value ?? "")) {
      return true;
    }
    return false;
  }, [
    listTitleField,
    listTitle,
    selectedTitleField,
    selectedTitle,
    featuredMaxField,
    featuredMaxValue,
    loadMoreField,
    loadMoreText,
  ]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((group) => {
      const form = projectGroupToForm(group);
      return (
        form.title.toLowerCase().includes(q) ||
        form.slug.toLowerCase().includes(q) ||
        form.tag.toLowerCase().includes(q)
      );
    });
  }, [projects, query]);

  async function saveFieldRecord(field, value) {
    await updateField(field.id, {
      label: field.label,
      fieldType: field.fieldType,
      key: field.key,
      isRichText: field.isRichText,
      isRequired: field.isRequired,
      isPublished: field.isPublished,
      sortOrder: field.sortOrder,
      value: value ?? "",
    });
  }

  async function saveProjectField(projectGroup, suffix, value) {
    const def = PROJECT_FIELD_DEFS.find((d) => d.suffix === suffix);
    if (!def) return;

    const field = projectGroup.fields[suffix];
    const nextValue = formValueToFieldString(def, value);

    if (field) {
      const currentValue = field.value ?? "";
      if (nextValue === currentValue) return;
      await saveFieldRecord(field, nextValue);
      return;
    }

    if (!nextValue && def.fieldType !== "boolean") return;

    const baseOrder = 20 + projectGroup.index * 50;
    const defIndex = PROJECT_FIELD_DEFS.findIndex((d) => d.suffix === suffix);

    await createField(sectionId, {
      key: `project_${projectGroup.index}_${suffix}`,
      label: `Project ${projectGroup.index} ${def.label}`,
      fieldType: def.fieldType,
      isRequired: false,
      sortOrder: baseOrder + (defIndex >= 0 ? defIndex : 0),
      value: nextValue,
    });
  }

  async function handleSaveSingleField(suffix, value) {
    if (!sectionId || editingGroup == null) return;

    const freshGroup =
      projects.find((p) => p.index === editingGroup.index) ?? editingGroup;

    await saveProjectField(freshGroup, suffix, value);
    await loadFields();
  }

  async function handleSaveProject(form) {
    if (!sectionId) return;

    if (form.featured) {
      const wasFeatured = editingGroup
        ? projectGroupToForm(editingGroup).featured
        : false;
      if (!wasFeatured && featuredCount >= featuredMax) {
        notify.error(`You can feature at most ${featuredMax} projects.`);
        return;
      }
    }

    setBusy(true);
    try {
      if (editingGroup) {
        const freshGroup =
          projects.find((p) => p.index === editingGroup.index) ?? editingGroup;
        const originalForm = projectGroupToForm(freshGroup);
        let savedAny = false;

        for (const def of PROJECT_FIELD_DEFS) {
          const newValue = form[def.suffix];
          const oldValue = originalForm[def.suffix];
          const same =
            def.fieldType === "boolean"
              ? Boolean(newValue) === Boolean(oldValue)
              : (newValue ?? "") === (oldValue ?? "");
          if (same) continue;

          await saveProjectField(freshGroup, def.suffix, newValue);
          savedAny = true;
        }

        notify.success(savedAny ? "Project updated" : "No changes to save");
      } else {
        const indices = projects.map((p) => p.index);
        const nextIndex = indices.length ? Math.max(...indices) + 1 : 0;
        const baseOrder = 20 + nextIndex * 50;

        for (let j = 0; j < PROJECT_FIELD_DEFS.length; j++) {
          const def = PROJECT_FIELD_DEFS[j];
          await createField(sectionId, {
            key: `project_${nextIndex}_${def.suffix}`,
            label: `Project ${nextIndex} ${def.label}`,
            fieldType: def.fieldType,
            isRequired: false,
            sortOrder: baseOrder + j,
            value: formValueToFieldString(def, form[def.suffix]),
          });
        }

        await syncProjectCount(sectionId, fields, [
          ...projects,
          { index: nextIndex, fields: {} },
        ]);
        notify.success("Project created");
      }

      setModalOpen(false);
      setEditingGroup(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !sectionId) return;
    setBusy(true);
    try {
      const ids = Object.values(deleteTarget.fields).map((f) => f.id);
      for (const id of ids) {
        await deleteField(id);
      }

      const remaining = projects.filter(
        (p) => p.index !== deleteTarget.index
      );
      await syncProjectCount(sectionId, fields, remaining);
      notify.success("Project deleted");
      setDeleteTarget(null);
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleFeatured(group) {
    const form = projectGroupToForm(group);
    const next = !form.featured;
    if (next && featuredCount >= featuredMax) {
      notify.error(`You can feature at most ${featuredMax} projects.`);
      return;
    }

    setBusy(true);
    try {
      await saveProjectField(group, "featured", next);
      notify.success(next ? "Project featured" : "Project unfeatured");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveAllSettings() {
    if (!hasSettingsChanges) return;
    setSavingSettings(true);
    try {
      if (listTitleField && listTitle !== (listTitleField.value ?? "")) {
        await saveFieldRecord(listTitleField, listTitle);
      }
      if (
        selectedTitleField &&
        selectedTitle !== (selectedTitleField.value ?? "")
      ) {
        await saveFieldRecord(selectedTitleField, selectedTitle);
      }
      if (
        featuredMaxField &&
        featuredMaxValue !== (featuredMaxField.value ?? "5")
      ) {
        await saveFieldRecord(featuredMaxField, featuredMaxValue);
      }
      if (loadMoreField && loadMoreText !== (loadMoreField.value ?? "")) {
        await saveFieldRecord(loadMoreField, loadMoreText);
      }
      notify.success("Changes saved");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setSavingSettings(false);
    }
  }

  if (sectionLoading) return <LoadingState message="Loading projects setup…" />;

  if (sectionError) {
    return (
      <div className="space-y-4">
        <ErrorState message={sectionError} onRetry={reloadSection} />
        {pageId ? (
          <Link
            to={`/cmsadmin/dashboard/pages/${pageId}/sections`}
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to {pageName} sections
          </Link>
        ) : (
          <Link
            to="/cmsadmin/dashboard/pages"
            className="inline-block text-sm font-medium text-slate-700 underline"
          >
            Go to Pages
          </Link>
        )}
      </div>
    );
  }

  if (loading) return <LoadingState message="Loading projects…" />;
  if (error) return <ErrorState message={error} onRetry={loadFields} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Work projects</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage case studies for the {pageName} page. Star projects to show in
            Selected projects (up to 3). More work lists every project (
            {featuredCount}/{featuredMax} starred).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-gray-50"
          >
            <IoSettingsOutline className="h-5 w-5" />
            {settingsOpen ? "Hide settings" : "Page settings"}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingGroup(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            <IoAddOutline className="h-5 w-5" />
            Add project
          </button>
        </div>
      </div>

      {settingsOpen ? (
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900">
            Work page section settings
          </h3>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {listTitleField ? (
              <FieldInputRenderer
                field={{ ...listTitleField, label: "More work title" }}
                value={listTitle}
                onChange={setListTitle}
              />
            ) : null}

            {selectedTitleField ? (
              <FieldInputRenderer
                field={selectedTitleField}
                value={selectedTitle}
                onChange={setSelectedTitle}
              />
            ) : null}

            {featuredMaxField ? (
              <label className="block text-sm">
                <span className="font-medium text-slate-700">
                  Max featured projects
                </span>
                <select
                  value={featuredMaxValue}
                  onChange={(e) => setFeaturedMaxValue(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                >
                  {FEATURED_MAX_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>
                      {n} projects
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {loadMoreField ? (
              <FieldInputRenderer
                field={loadMoreField}
                value={loadMoreText}
                onChange={setLoadMoreText}
              />
            ) : null}
          </div>

          <div className="flex justify-end border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={saveAllSettings}
              disabled={savingSettings || !hasSettingsChanges}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {savingSettings ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : null}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by title, slug, or tag…"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />

      {filtered.length === 0 ? (
        <EmptyState
          title={query ? "No matching projects" : "No projects yet"}
          description={
            query
              ? "Try a different search term."
              : "Create your first project to show on the Work page."
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Project
                </th>
                <th className="hidden px-4 py-3 text-left font-medium text-slate-600 md:table-cell">
                  Tag
                </th>
                <th className="px-4 py-3 text-center font-medium text-slate-600">
                  Featured
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((group) => {
                const form = projectGroupToForm(group);
                const imageSrc = resolveMediaUrl(form.image);
                const isFeatured = Boolean(form.featured);

                return (
                  <tr key={group.index} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            className="h-12 w-16 shrink-0 rounded-lg border border-gray-200 object-cover"
                          />
                        ) : (
                          <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-slate-50 text-xs text-slate-400">
                            No img
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">
                            {form.title || getProjectTitle(group)}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            /projectdetail/{form.slug || `project-${group.index}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 md:table-cell">
                      {form.tag || "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => toggleFeatured(group)}
                        disabled={busy}
                        className={`inline-flex items-center justify-center rounded-lg border p-2 disabled:opacity-50 ${
                          isFeatured
                            ? "border-amber-200 bg-amber-50 text-amber-600"
                            : "border-gray-200 text-slate-400 hover:bg-gray-50"
                        }`}
                        aria-label={
                          isFeatured ? "Remove from featured" : "Mark as featured"
                        }
                      >
                        {isFeatured ? (
                          <IoStar className="h-4 w-4" />
                        ) : (
                          <IoStarOutline className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroup(group);
                            setModalOpen(true);
                          }}
                          className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                          aria-label={`Edit ${form.title}`}
                        >
                          <IoCreateOutline className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(group)}
                          className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                          aria-label={`Delete ${form.title}`}
                        >
                          <IoTrashOutline className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProjectFormModal
        open={modalOpen}
        sectionId={sectionId}
        initialForm={editingGroup ? projectGroupToForm(editingGroup) : null}
        editingProjectKey={editingGroup?.index ?? null}
        isEdit={Boolean(editingGroup)}
        busy={busy}
        featuredMax={featuredMax}
        featuredCount={featuredCount}
        onClose={() => {
          setModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleSaveProject}
        onSaveField={editingGroup ? handleSaveSingleField : undefined}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete project?"
        message={
          deleteTarget
            ? `This will permanently delete "${getProjectTitle(deleteTarget)}".`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
